import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, Calendar, MapPin, Phone } from 'lucide-react'
import { Page, PageHeader, Card, SkeletonRows, EmptyState, btnPrimary, btnSecondary, useConfirm } from '@/components/rm'
import { useAuth } from '@/hooks/use-auth'
import { displayPhMobile, fmtDate, fmtDateTime, fmtTime, fmtWeekday, pesoFromCentavos } from '@/lib/ph'
import { useBookingActions, useBookingsForCompany, type BusinessBooking, type BookingStatus } from '@/lib/bookings'
import { cn, errorText } from '@/lib/utils'
import { useBookingMoney } from '@/lib/escrow'
import { REASON_EN, useBookingDispute } from '@/lib/disputes'
import { ReportProblem } from '@/components/dispute'

export const PAYOUT_LABEL = { waiting_account: 'add your payout account to receive it', queued: 'queued for sending', sent: 'sent' } as const

// Spec 007. The business's MySerbisyo bookings: answer requests, see confirmed and paid bookings.
// English first (business tools).
export const Route = createFileRoute('/_app/mkt-bookings')({ component: Bookings })

const LABEL: Record<BookingStatus, string> = {
  requested: 'Request', awaiting_payment: 'Waiting for payment', paid: 'Paid, held', done: 'Done, waiting for the client',
  released: 'Completed', disputed: 'Dispute', refunded: 'Refunded', cancelled: 'Cancelled', expired: 'Expired',
}

function Bookings() {
  const q = useBookingsForCompany()
  const { role } = useAuth()
  const canAnswer = role === 'company-admin'
  const { respond, cancel } = useBookingActions()
  const money = useBookingMoney()
  const [confirm, confirmDialog] = useConfirm()
  const requests = (q.data ?? []).filter((b) => b.status === 'requested')
  const rest = (q.data ?? []).filter((b) => b.status !== 'requested')

  const act = async (p: Promise<{ ok: boolean; error?: string }>, ok: string) => {
    try {
      const r = await p
      if (r.ok) toast.success(ok)
      else toast.error(r.error === 'time_passed' ? 'That time has passed. Ask the client for a new time.' : r.error === 'not_open' ? 'This request is no longer open.' : 'Could not update the booking')
    } catch (e) { toast.error(errorText(e, 'Could not update the booking')) }
  }

  const row = (b: BusinessBooking) => (
    <div key={b.id} className="rounded-[14px] border border-token-subtle bg-token-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-token-primary">{b.title}</p>
          <p className="text-[12.5px] text-token-secondary">{b.client_first_name} · booked {fmtDateTime(b.created_at)}</p>
        </div>
        <div className="flex flex-none flex-col items-end gap-1">
          <span className="text-[15px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(b.price_centavos)}</span>
          <span className={cn('rounded-full px-2.5 py-0.5 text-[12px] font-semibold', b.status === 'requested' ? 'bg-token-info-surface text-token-info' : b.status === 'paid' ? 'bg-token-success-surface text-token-success' : 'bg-token-hover text-token-secondary')}>{LABEL[b.status]}</span>
        </div>
      </div>
      <div className="mt-2 space-y-1 text-[13px] text-token-primary">
        <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-token-muted" />{fmtWeekday(b.scheduled_start)}, {fmtTime(b.scheduled_start)} · {b.duration_min} min</p>
        <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-token-muted" />{[b.address_line, b.barangay, b.city].filter(Boolean).join(', ')}{!b.address_line && <span className="text-token-muted"> · street address after payment</span>}</p>
        {b.client_mobile && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-token-muted" />{displayPhMobile(b.client_mobile)}</p>}
        {b.outside_area && b.status === 'requested' && <p className="flex items-center gap-2 font-semibold text-token-warning"><AlertTriangle className="h-4 w-4" />Outside your usual area</p>}
      </div>
      {b.note && <p className="mt-2 whitespace-pre-line text-[13px] text-token-secondary">“{b.note}”</p>}
      {b.status === 'done' && b.auto_release_at && <p className="mt-2 text-[12.5px] text-token-secondary">Waiting for the client to confirm. Released automatically on {fmtDateTime(b.auto_release_at)} if they do nothing.</p>}
      {b.payout && <p className="mt-2 text-[12.5px] font-semibold text-token-success">Your payout: {pesoFromCentavos(b.payout.amount_centavos)} · {PAYOUT_LABEL[b.payout.status]}</p>}
      {b.status === 'refunded' && <p className="mt-2 text-[12.5px] text-token-muted">Refunded {pesoFromCentavos(b.refund_centavos ?? 0)} to the client.</p>}
      {['disputed', 'released', 'refunded'].includes(b.status) && <BusinessDispute bookingId={b.id} />}
      {(b.status === 'cancelled' || b.status === 'expired') && b.cancel_reason && <p className="mt-2 text-[12.5px] text-token-muted">{b.cancel_reason}</p>}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {b.job_id && <Link to="/jobs/$id" params={{ id: String(b.job_id) }} className={btnSecondary}>Open job</Link>}
        {canAnswer && b.status === 'requested' && <>
          <button className={btnSecondary} disabled={respond.isPending} onClick={async () => {
            const ok = await confirm({ title: 'Decline this request?', body: 'The client is told and can book someone else.', confirmLabel: 'Decline', tone: 'danger' })
            if (ok) await act(respond.mutateAsync({ booking: b.id, accept: false }), 'Request declined')
          }}>Decline</button>
          <button className={btnPrimary} disabled={respond.isPending} onClick={() => act(respond.mutateAsync({ booking: b.id, accept: true }), 'Accepted. It is on your schedule.')}>Accept</button>
        </>}
        {b.status === 'paid' && (
          <button className={btnPrimary} disabled={money.markDone.isPending} onClick={async () => {
            const ok = await confirm({ title: 'Mark this job done?', body: 'The client is asked to confirm. If they do nothing, the payment is released automatically after 72 hours, not counting holidays.', confirmLabel: 'Mark done' })
            if (ok) await act(money.markDone.mutateAsync(b.id), 'Marked done. Waiting for the client.')
          }}>Mark done</button>
        )}
        {canAnswer && b.status === 'paid' && (
          <button className={btnSecondary} disabled={money.cancelPaid.isPending} onClick={async () => {
            const ok = await confirm({ title: 'Cancel and refund in full?', body: 'The client gets back everything they paid. Use this if you cannot do the job.', confirmLabel: 'Cancel and refund', tone: 'danger' })
            if (!ok) return
            try { await money.cancelPaid.mutateAsync({ booking: b.id }); toast.success('Cancelled. The client is refunded in full.') } catch (e) { toast.error(errorText(e, 'The refund could not be made')) }
          }}>Cancel and refund</button>
        )}
        {(b.status === 'paid' || b.status === 'done') && <ReportProblem bookingId={b.id} english />}
        {canAnswer && b.status === 'awaiting_payment' && (
          <button className={btnSecondary} onClick={async () => {
            const ok = await confirm({ title: 'Cancel this booking?', body: 'Nothing has been paid. The client is told.', confirmLabel: 'Cancel booking', tone: 'danger' })
            if (ok) await act(cancel.mutateAsync({ booking: b.id }), 'Booking cancelled')
          }}>Cancel</button>
        )}
      </div>
    </div>
  )

  return (
    <Page>
      {confirmDialog}
      <PageHeader title="Bookings" subtitle="MySerbisyo bookings. Each confirmed booking is also a job on your schedule." />
      {q.isLoading && <Card className="mt-5"><SkeletonRows /></Card>}
      {!q.isLoading && (q.data ?? []).length === 0 && <Card className="mt-5"><EmptyState label="No bookings yet. Clients book the fixed-price services on your listing, or accept your offers." /></Card>}
      {requests.length > 0 && <section className="mt-5"><h2 className="mb-2 text-[12px] font-bold uppercase tracking-[0.06em] text-token-muted">Requests to answer</h2><div className="space-y-3">{requests.map(row)}</div></section>}
      {rest.length > 0 && <section className="mt-5"><h2 className="mb-2 text-[12px] font-bold uppercase tracking-[0.06em] text-token-muted">Bookings</h2><div className="space-y-3">{rest.map(row)}</div></section>}
    </Page>
  )
}

/** Spec 009: the dispute on a booking, as the business sees it. */
function BusinessDispute({ bookingId }: { bookingId: string }) {
  const q = useBookingDispute(bookingId)
  const d = q.data
  if (!d) return null
  if (d.status !== 'decided') {
    return (
      <div className="mt-2 rounded-[10px] bg-token-danger-surface px-3 py-2 text-[12.5px] text-token-primary">
        <p className="font-semibold">{d.raised_by_side === 'business' ? 'You reported a problem' : 'The client reported a problem'}: {REASON_EN[d.reason]}</p>
        <p className="text-token-secondary">“{d.description}”</p>
        <p className="text-token-secondary">A MySerbisyo admin will decide by {fmtDate(d.due_at, 'long')}. The payment stays held until then. You can still message the client.</p>
      </div>
    )
  }
  const text = d.decision === 'refund' ? `Decided: the client was refunded ${pesoFromCentavos(d.refund_centavos ?? 0)}.`
    : d.decision === 'split' ? `Decided: the client was refunded ${pesoFromCentavos(d.refund_centavos ?? 0)}. The rest was released to you.`
    : 'Decided: the payment was released to you.'
  return (
    <div className="mt-2 rounded-[10px] bg-token-hover px-3 py-2 text-[12.5px] text-token-primary">
      <p className="font-semibold">{text}</p>
      {d.decision_note && <p className="text-token-secondary">Admin note: {d.decision_note}</p>}
    </div>
  )
}
